import React, { useEffect, useMemo, useState } from 'react';
import { FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import SearchableDropdown from '../../../../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import statusWorkflowService from '../../services/statusWorkflowService';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';

const TIME_UNITS = ['seconds', 'minutes', 'hours', 'days', 'months'];

export default function UpdateStatusSchedulerForm({ form, formId, onChange, errors = {} }) {
  const copy = FORM_STUDIO_COPY.automations.scheduled;
  const [statusList, setStatusList] = useState([]);
  const [typeFilter, setTypeFilter] = useState({ label: 'All', value: '' });

  useEffect(() => {
    if (!formId) return;
    statusWorkflowService
      .fetchStatuses({ leadFormId: formId })
      .then((response) => {
        setStatusList(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => setStatusList([]));
  }, [formId]);

  const typeOptions = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(statusList.map((item) => item.type_of_status).filter(Boolean))
    );
    return [
      { label: 'All', value: '' },
      ...uniqueTypes.map((type) => ({ label: type, value: type })),
    ];
  }, [statusList]);

  const filteredStatuses = useMemo(() => {
    const typeValue = typeFilter?.value;
    if (!typeValue) return statusList;
    return statusList.filter((item) => item.type_of_status === typeValue);
  }, [statusList, typeFilter]);

  const update = (patch) => onChange?.({ ...form, ...patch });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">{copy.whenSection}</Typography>
      <SearchableDropdown
        options={typeOptions}
        label={copy.typeOfStatusFilter}
        value={typeFilter}
        onChange={(val) => setTypeFilter(val || { label: 'All', value: '' })}
      />
      <SearchableDropdown
        options={filteredStatuses}
        labelKey="status_name"
        valueKey="status_id"
        label={copy.currentStatus}
        value={form.currentStatus ?? null}
        onChange={(val) =>
          update({
            currentStatus: val
              ? { status_id: val.status_id, status_name: val.status_name }
              : null,
          })
        }
      />

      <Typography variant="subtitle2">{copy.afterSection}</Typography>
      <TextField
        label={copy.delayValue}
        size="small"
        type="number"
        fullWidth
        value={form.trigger_delay ?? 1}
        onChange={(event) => update({ trigger_delay: Number(event.target.value) })}
        error={Boolean(errors.trigger_delay)}
        helperText={errors.trigger_delay}
      />
      <TextField
        select
        label={copy.delayUnit}
        size="small"
        fullWidth
        value={form.trigger_delay_unit || 'days'}
        onChange={(event) => update({ trigger_delay_unit: event.target.value })}
      >
        {TIME_UNITS.map((unit) => (
          <MenuItem key={unit} value={unit}>
            {unit}
          </MenuItem>
        ))}
      </TextField>

      <Typography variant="subtitle2">{copy.thenSection}</Typography>
      <SearchableDropdown
        options={filteredStatuses}
        labelKey="status_name"
        valueKey="status_id"
        label={copy.nextStatus}
        value={form.nextStatus ?? null}
        getOptionDisabled={(option) =>
          option.status_id === form.currentStatus?.status_id
        }
        onChange={(val) =>
          update({
            nextStatus: val
              ? { status_id: val.status_id, status_name: val.status_name }
              : null,
          })
        }
      />

      <TextField
        label={copy.description}
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={form.description || ''}
        onChange={(event) => update({ description: event.target.value })}
      />

      <FormControlLabel
        control={
          <Switch
            checked={form.active !== false}
            onChange={(event) => update({ active: event.target.checked })}
          />
        }
        label={copy.activeLabel}
      />
    </Stack>
  );
}
